import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

import { GroupResource } from "../../api/groups";

interface GroupStatusDialogProps {
  open: boolean;
  groupId: string;
  groupName: string;
  onClose: () => void;
}

export const GroupStatusDialog: React.FC<GroupStatusDialogProps> = ({
  open,
  groupId,
  groupName,
  onClose,
}) => {
  const [status, setStatus] = React.useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();

  React.useEffect(() => {
    if (!open) return;
    setStatus(null);
    setError(undefined);
    setLoading(true);

    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const dateStartUtc = weekAgo.toISOString();
    const dateEndUtc = now.toISOString();

    GroupResource.getStatus(groupId, dateStartUtc, dateEndUtc)
      .then(setStatus)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Failed to load status.")
      )
      .finally(() => setLoading(false));
  }, [open, groupId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Status — {groupName}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {!loading && status && (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1,
              fontSize: "0.8rem",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(status, null, 2)}
          </Box>
        )}
        {!loading && !error && status && Object.keys(status).length === 0 && (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 2 }}>
            No status data available for this period.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
